import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const BrandPerformanceChart = ({ data, loading }) => {
  const mockData = [
    { 
      brand: 'Coca-Cola', 
      totalCost: 1250000, 
      installations: 45, 
      avgCostPerInstallation: 27778,
      roi: 8.5,
      completionRate: 95
    },
    { 
      brand: 'Pepsi', 
      totalCost: 980000, 
      installations: 38, 
      avgCostPerInstallation: 25789,
      roi: 7.2,
      completionRate: 92
    },
    { 
      brand: 'Samsung', 
      totalCost: 1450000, 
      installations: 32, 
      avgCostPerInstallation: 45313,
      roi: 12.3,
      completionRate: 98
    },
    { 
      brand: 'LG', 
      totalCost: 1120000, 
      installations: 28, 
      avgCostPerInstallation: 40000,
      roi: 9.8,
      completionRate: 89
    },
    { 
      brand: 'Nestlé', 
      totalCost: 850000, 
      installations: 35, 
      avgCostPerInstallation: 24286,
      roi: 6.5,
      completionRate: 94
    }
  ];

  const chartData = data || mockData;

  const formatCurrency = (value) => {
    return `৳${(value / 1000)?.toFixed(0)}K`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-surface border border-border rounded-lg p-4 shadow-elevated min-w-64">
          <p className="font-semibold text-foreground mb-3">{label}</p>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground text-sm">Total Cost:</span>
              <span className="font-medium text-foreground">{formatCurrency(data?.totalCost)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground text-sm">Installations:</span>
              <span className="font-medium text-foreground">{data?.installations}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground text-sm">Avg Cost/Install:</span>
              <span className="font-medium text-foreground">৳{data?.avgCostPerInstallation?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground text-sm">ROI:</span>
              <span className="font-medium text-success">{data?.roi}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground text-sm">Completion Rate:</span>
              <span className="font-medium text-foreground">{data?.completionRate}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-80">
          <div className="flex items-center gap-3 text-muted-foreground">
            <div className="animate-spin">
              <Icon name="Loader2" size={20} />
            </div>
            <span>Loading brand performance data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Brand Performance Comparison</h3>
          <p className="text-sm text-muted-foreground">Cost analysis and ROI metrics by brand</p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary rounded-full" />
            <span className="text-muted-foreground">Total Cost</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-secondary rounded-full" />
            <span className="text-muted-foreground">Installations</span>
          </div>
        </div>
      </div>

      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="brand" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              yAxisId="cost"
              orientation="left"
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={formatCurrency}
            />
            <YAxis 
              yAxisId="count"
              orientation="right"
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              yAxisId="cost"
              dataKey="totalCost"
              fill="var(--color-primary)"
              radius={[4, 4, 0, 0]}
              name="Total Cost"
            />
            <Bar
              yAxisId="count"
              dataKey="installations"
              fill="var(--color-secondary)"
              radius={[4, 4, 0, 0]}
              name="Installations"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BrandPerformanceChart;